/**
 * 集中式部署配置解析。
 *
 * 鉴权参数（服务端地址/用户名/密码/租户 ID）与报表编码、名称等「部署参数」
 * 不应每次都在命令行重复传入，也不应硬编码进仓库。
 *
 * 解析优先级（高 → 低）：
 *   1. 命令行 flag（--base-url / --username / --password / --tenantId / --name）
 *   2. 环境变量（BASK_BASE_URL / BASK_USERNAME / BASK_PASSWORD / BASK_TENANT_ID / BASK_NAME）
 *   3. 项目根目录下的 .env 文件（同名大写键，如 BASK_BASE_URL=...）
 *   4. 项目根目录下的 baskreport.config.json（{ "baseUrl": ..., ... }）
 *
 * 配置查找目录定位优先级：--config-dir / BASK_CONFIG_DIR > 以 cwd 向上查找含 .env/.config.json 的目录 > cwd 本身。
 * 凭证优先放 .env（已被 gitignore 忽略，绝不进仓库）；作为兜底也可写入 baskreport.config.json，
 * 但需注意该文件可能进仓库，务必自行 gitignore 或只放非敏感默认值。
 */

import { existsSync, readFileSync, writeFileSync } from 'node:fs';
import { fileURLToPath } from 'node:url';
import { dirname, resolve } from 'node:path';

const __dirname = dirname(fileURLToPath(import.meta.url));

/**
 * 定位用户项目根目录（含 .env / baskreport.config.json 的目录）。
 *
 * 作为 skill / 全局 npm 依赖部署后，本文件被打进 node_modules 下的 dist，
 * __dirname 已不再是用户项目，故**不能再以 __dirname 上溯定位用户项目**。
 * 定位优先级：
 *   1. 显式 --config-dir <dir> / 环境变量 BASK_CONFIG_DIR（AI client 注入最可靠）
 *   2. 以 process.cwd() 为锚点，向上查找含 .env 或 baskreport.config.json 的目录
 *   3. 回退到 process.cwd() 本身（最常见场景：用户就在项目根目录执行）
 */
let _configDirOverride: string | undefined;
/** 供 CLI 注入显式配置目录（--config-dir / BASK_CONFIG_DIR）。 */
export function setConfigDir(dir: string): void {
  _configDirOverride = resolve(dir);
}
/** 当前生效的配置查找目录（用于诊断输出）。 */
export function getConfigDir(): string {
  return PROJECT_ROOT;
}

function findProjectRoot(): string {
  if (_configDirOverride) return _configDirOverride;
  const fromEnv = process.env.BASK_CONFIG_DIR?.trim();
  if (fromEnv) return resolve(fromEnv);

  const cwd = process.cwd();
  let dir = cwd;
  for (;;) {
    if (
      existsSync(resolve(dir, '.env')) ||
      existsSync(resolve(dir, 'baskreport.config.json'))
    ) {
      return dir;
    }
    const parent = dirname(dir);
    if (parent === dir) break; // 已到文件系统根
    dir = parent;
  }
  return cwd;
}

const ROOT = findProjectRoot();
/** 项目根目录（存放 .env / baskreport.config.json 的目录）。 */
const PROJECT_ROOT = ROOT;

/** 是否处于非交互模式（AI client 驱动，无 TTY）。影响确认/租户选择行为。 */
export function isNonInteractive(): boolean {
  return (
    process.env.BASK_NONINTERACTIVE === '1' ||
    process.env.BASK_NONINTERACTIVE === 'true' ||
    process.env.CI === 'true'
  );
}

/**
 * 从 commander 解析结果里抽取 --config-dir 并注入配置查找目录。
 * 在任意子命令 action 开头调用一次即可；缺省时回退到 cwd 向上查找。
 * 接受 commander 的 opts 对象（含 configDir 字段）或 undefined。
 */
export function applyConfigDir(opts?: { configDir?: string }): void {
  const dir = opts?.configDir?.trim();
  if (dir) setConfigDir(dir);
}

/** OAuth2 模式下 token 注入的默认 header 名（值形如 `Bearer <token>`）。 */
export const DEFAULT_ACCESS_TOKEN_HEADER = 'authorization';
/** header 名可自定义时，token 值的前缀（如后端要求裸 token，可设为空字符串）。 */
export const DEFAULT_ACCESS_TOKEN_PREFIX = 'Bearer ';

export interface AuthConfig {
  baseUrl: string;
  username: string;
  password: string;
  tenantId?: string;
  /** OAuth2 访问令牌。与 username/password 互斥：二者恰好提供其一。 */
  accessToken?: string;
  /** OAuth2 token 注入的 header 名，缺省 `authorization`（值形如 `Bearer <token>`）。 */
  accessTokenHeader?: string;
}

/** 判断鉴权配置是否为 OAuth2（accessToken）模式。 */
export function isOAuth2(auth: Partial<AuthConfig>): boolean {
  return !!(auth.accessToken && auth.accessToken.trim());
}

/** 判断鉴权配置是否为 Session（username/password）模式。 */
export function isSessionAuth(auth: Partial<AuthConfig>): boolean {
  return !!(auth.username && auth.username.trim()) && !!(auth.password && auth.password.trim());
}

export interface DeployConfig extends AuthConfig {
  name?: string;
  /** 探查后缓存的默认数据源 ID（仅单数据源场景自动记录，供后续命令省略判断）。 */
  datasourceId?: string;
}

/** 极简 .env 解析（不引第三方依赖）。仅支持 KEY=VALUE，忽略 # 注释与空行。 */
function loadDotEnv(): Record<string, string> {
  const envPath = resolve(ROOT, '.env');
  if (!existsSync(envPath)) return {};
  const text = readFileSync(envPath, 'utf-8');
  const out: Record<string, string> = {};
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;
    const eq = line.indexOf('=');
    if (eq < 0) continue;
    const key = line.slice(0, eq).trim();
    let val = line.slice(eq + 1).trim();
    // 去除成对的引号
    if (
      (val.startsWith('"') && val.endsWith('"')) ||
      (val.startsWith("'") && val.endsWith("'"))
    ) {
      val = val.slice(1, -1);
    }
    if (key) out[key] = val;
  }
  return out;
}

/** 读取项目根 baskreport.config.json（可放非敏感默认值；作为兜底也可放 baseUrl/username/password，但需注意该文件可能进仓库）。 */
function loadJsonConfig(): Record<string, unknown> {
  const cfgPath = resolve(ROOT, 'baskreport.config.json');
  if (!existsSync(cfgPath)) return {};
  try {
    return JSON.parse(readFileSync(cfgPath, 'utf-8'));
  } catch {
    return {};
  }
}

let cached: { env: Record<string, string>; json: Record<string, unknown> } | null = null;

function sources() {
  if (!cached) cached = { env: loadDotEnv(), json: loadJsonConfig() };
  return cached;
}

function pickString(...vals: (string | undefined)[]): string | undefined {
  for (const v of vals) if (v && v.trim()) return v.trim();
  return undefined;
}

/**
 * 解析鉴权配置。flag 来自 commander 的 opts；其余回退到环境变量/.env/json。
 * 返回的四个字段可能仍缺失，由调用方按「是否需要登录」决定报错。
 */
export function resolveAuth(flag: Partial<AuthConfig>): AuthConfig {
  const { env, json } = sources();
  const baseUrl = pickString(
    flag.baseUrl,
    process.env.BASK_BASE_URL,
    env.BASK_BASE_URL,
    json.baseUrl as string | undefined,
  );
  const username = pickString(
    flag.username,
    process.env.BASK_USERNAME,
    env.BASK_USERNAME,
    json.username as string | undefined,
  );
  const password = pickString(
    flag.password,
    process.env.BASK_PASSWORD,
    env.BASK_PASSWORD,
    json.password as string | undefined,
  );
  const tenantId = pickString(
    flag.tenantId,
    process.env.BASK_TENANT_ID,
    env.BASK_TENANT_ID,
    json.tenantId as string | undefined,
  );
  const accessToken = pickString(
    flag.accessToken,
    process.env.BASK_ACCESS_TOKEN,
    env.BASK_ACCESS_TOKEN,
    json.accessToken as string | undefined,
  );
  const accessTokenHeader = pickString(
    flag.accessTokenHeader,
    process.env.BASK_ACCESS_TOKEN_HEADER,
    env.BASK_ACCESS_TOKEN_HEADER,
    json.accessTokenHeader as string | undefined,
  );
  return {
    baseUrl: baseUrl ?? '',
    username: username ?? '',
    password: password ?? '',
    tenantId,
    accessToken,
    accessTokenHeader,
  };
}

/** 解析部署配置（鉴权 + 名称）。报表定位统一由 --id 确定，不再使用 code。 */
export function resolveDeploy(
  flag: Partial<DeployConfig>,
): DeployConfig {
  const auth = resolveAuth(flag);
  const { env, json } = sources();
  const name = pickString(flag.name, process.env.BASK_NAME, env.BASK_NAME, json.name as string | undefined);
  return { ...auth, name };
}

/** 需要登录时校验鉴权必填项，缺失给出可读错误。两种登录方式互斥。 */
export function requireAuth(auth: AuthConfig, ctx: string): void {
  const miss: string[] = [];
  if (!auth.baseUrl) miss.push('baseUrl');

  const oauth2 = isOAuth2(auth);
  const session = isSessionAuth(auth);

  // 凭证二选一：OAuth2（accessToken）与 Session（username+password）互斥，恰好提供其一。
  if (!oauth2 && !session) {
    throw new Error(
      `${ctx} 缺少鉴权参数：必须提供「accessToken」或「username+password」之一（二者互斥）。\n` +
        `当前配置查找目录：${PROJECT_ROOT}\n` +
        `  - 方式一（OAuth2）：提供 --access-token / BASK_ACCESS_TOKEN（token 经 Authorization header 携带）\n` +
        `  - 方式二（Session）：提供 --username / --password 以及对应的 BASK_USERNAME / BASK_PASSWORD\n` +
        `  - 请确保 .env 位于该目录，或通过 --config-dir <项目目录> / 环境变量 BASK_CONFIG_DIR 指定`,
    );
  }
  if (oauth2 && session) {
    throw new Error(
      `${ctx} 鉴权参数冲突：accessToken（OAuth2）与 username/password（Session）互斥，请勿同时提供。\n` +
        `当前配置查找目录：${PROJECT_ROOT}\n` +
        `  - 使用 OAuth2 时只传 --access-token / BASK_ACCESS_TOKEN，不要传 --username/--password\n` +
        `  - 使用 Session 登录时只传 --username/--password，不要传 --access-token`,
    );
  }

  // tenantId 允许为空：登录后会调用 /baskserver/api/user/get 自动解析（仅一个租户时默认采用）。
  // 仅当多租户或获取失败才需在 --tenantId / BASK_TENANT_ID / .env 显式指定，故这里不强制。
  if (miss.length) {
    throw new Error(
      `${ctx} 缺少鉴权参数：${miss.join(', ')}。\n` +
        `当前配置查找目录：${PROJECT_ROOT}\n` +
        `  - 请确保 .env 位于该目录，或通过 --config-dir <项目目录> / 环境变量 BASK_CONFIG_DIR 指定\n` +
        `  - 或命令行显式传 --${miss
          .map((m) => (m === 'baseUrl' ? 'base-url' : m))
          .join(' / --')}\n` +
        `  - 或在环境变量 / .env 中配置 BASK_${miss
          .map((m) => (m === 'baseUrl' ? 'BASE_URL' : m.toUpperCase()))
          .join(' / BASK_')}。`,
    );
  }
}

/**
 * 读取缓存的默认数据源 ID（来自 baskreport.config.json 的 datasourceId）。
 * 仅当 `load-all-datasource-defs` 探测到唯一数据源时才会被写入，
 * 供后续命令在省略 --datasource-id 时回退使用，避免重复判断。
 */
export function resolveDefaultDatasourceId(): string | undefined {
  const { json } = sources();
  const id = json.datasourceId as string | undefined;
  return id && id.trim() ? id.trim() : undefined;
}

/**
 * 把默认数据源 ID 写入 baskreport.config.json（合并已有字段，不触碰密码）。
 * 仅在探测到唯一数据源时调用，作为后续命令的默认上下文。
 */
export function saveDefaultDatasourceId(datasourceId: string): void {
  const cfgPath = resolve(ROOT, 'baskreport.config.json');
  const prev = existsSync(cfgPath)
    ? (() => {
        try {
          return JSON.parse(readFileSync(cfgPath, 'utf-8'));
        } catch {
          return {};
        }
      })()
    : {};
  const next = { ...prev, datasourceId };
  writeFileSync(cfgPath, JSON.stringify(next, null, 2) + '\n', 'utf-8');
}
